import { createReducer } from 'typesafe-actions';

import { TWalletAction, WalletActionTypes } from '../types';

import { IState } from '@/store/types/search.types';
import { IProfile, IProfileWallet } from '@/store/models/profile.model';
import { IProfileState } from '@/store/types/profile.types';
import { IHandleAsynProfileSelectors, IHandleAsynProfileActions } from '@/features/user/store/profile.types';
import { IOperationProofOfPayoutDownload } from '@/features/wallets/store/models/wallet.model';
import { TAppError } from '@/store/types/base.state';

const initialState: IProfileState = {
  profiles: {
    state: [],
  },
};

export const walletReducer = createReducer<IProfileState, TWalletAction>(initialState)
  /**
   * Create wallet reducers
   */
  .handleType(WalletActionTypes.CREATE_WALLET, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { wallets: { create: {} } }),
  )

  .handleType(WalletActionTypes.CREATE_WALLET_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { wallets: { create: {} } }, { error: action.error }),
  )

  .handleType(WalletActionTypes.CREATE_WALLET_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile) => {
      if (profile.state?.user.state?.userId === action.wallet.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileWallets: {
              ...profile.state?.profileWallets,
              state: [{ state: { wallet: action.wallet } }, ...(profile.state?.profileWallets?.state || [])],
              create: {
                isLoading: false,
                error: undefined,
              },
              currentWalletId: action.wallet.walletId,
              error: undefined,
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Update wallet reducers
   */
  .handleType(WalletActionTypes.UPDATE_WALLET, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { wallets: { walletId: action.walletId } }),
  )

  .handleType(WalletActionTypes.UPDATE_WALLET_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId } },
      { error: action.error },
    ),
  )

  .handleType(WalletActionTypes.UPDATE_WALLET_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.wallet.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileWallets: {
              ...profile.state?.profileWallets,
              state: profile.state?.profileWallets?.state?.map((profileWallet) => {
                if (profileWallet.state?.wallet && profileWallet.state?.wallet.walletId === action.wallet.walletId) {
                  return {
                    ...profileWallet,
                    state: {
                      ...profileWallet.state,
                      wallet: action.wallet,
                    },
                    isLoading: false,
                    error: undefined,
                  };
                }
                return profileWallet;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * simulate SCTR
   */
  .handleType(WalletActionTypes.SIMULATE_SCTR, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      wallets: { walletId: action.walletId, simulation: { sctr: {} } },
    }),
  )

  .handleType(WalletActionTypes.SIMULATE_SCTR_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, simulation: { sctr: {} } } },
      { error: action.error },
    ),
  )

  .handleType(WalletActionTypes.SIMULATE_SCTR_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      wallets: { walletId: action.walletId, simulation: { sctr: {} } },
    }),
  )

  /**
   * simulate Operation
   */
  .handleType(WalletActionTypes.SIMULATE_OPERATION, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      wallets: { walletId: action.walletId, simulation: { operation: {} } },
    }),
  )

  .handleType(WalletActionTypes.SIMULATE_OPERATION_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, simulation: { operation: {} } } },
      { error: action.error },
    ),
  )

  .handleType(WalletActionTypes.SIMULATE_OPERATION_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      wallets: { walletId: action.walletId, simulation: { operation: {} } },
    }),
  )

  /**
   * Request Operations report
   */
  .handleType(WalletActionTypes.REQUEST_OPERATIONS_REPORT, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, report: {} } },
      { error: undefined },
    ),
  )

  .handleType(WalletActionTypes.REQUEST_OPERATIONS_REPORT_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, report: {} } },
      { error: action.error },
    ),
  )

  .handleType(WalletActionTypes.REQUEST_OPERATIONS_REPORT_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, report: {} } },
      { error: undefined, state: action.operation },
    ),
  )

  /**
   * GET Operations report
   */

  .handleType(WalletActionTypes.GET_OPERATIONS_REPORT, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, report: {} } },
      { error: undefined },
    ),
  )

  .handleType(WalletActionTypes.GET_OPERATIONS_REPORT_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, report: {} } },
      { error: action.error },
    ),
  )

  .handleType(WalletActionTypes.GET_OPERATIONS_REPORT_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, report: {} } },
      { error: undefined, state: action.operation },
    ),
  )

  /**
   * Reset Operation Report reducers
   */
  .handleType(WalletActionTypes.RESET_OPERATIONS_REPORT, (state, action) => {
    const currentOperationsReport = state.profiles.state?.find(
      (profile) => profile.state?.user.state?.userId === action.userId,
    )?.state?.operations?.state;
    if (currentOperationsReport) {
      delete currentOperationsReport[action.walletId];
    }
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            operations: {
              state: {
                ...currentOperationsReport,
              },
              isLoading: false,
              error: undefined,
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Search Wallet reducers
   */
  .handleType(WalletActionTypes.GET_WALLET, (state, action) =>
    handleAsyncProfileReducer(state, action.walletId, { wallets: {} }),
  )

  .handleType(WalletActionTypes.GET_WALLET_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.walletId, { wallets: {} }, { error: action.error }),
  )

  .handleType(WalletActionTypes.GET_WALLET_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state && profile.state.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileWallets: {
              ...profile.state.profileWallets,
              state: profile.state?.profileWallets?.state?.map((profileWallet) => {
                if (profileWallet.state?.wallet && profileWallet.state?.wallet.walletId === action.walletId) {
                  return {
                    ...profileWallet,
                    state: {
                      ...profileWallet.state,
                      wallet: action.wallet,
                    },
                  };
                }

                return profileWallet;
              }),
              isLoading: false,
              error: undefined,
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Search Wallets reducers
   */
  .handleType(WalletActionTypes.GET_WALLETS, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { wallets: {} }),
  )

  .handleType(WalletActionTypes.GET_WALLETS_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { wallets: {} }, { error: action.error }),
  )

  .handleType(WalletActionTypes.GET_WALLETS_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state && profile.state.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileWallets: {
              ...profile.state.profileWallets,
              state: action.wallets.map((wallet) => ({
                state: { wallet },
              })),
              isLoading: false,
              error: undefined,
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Get Current Wallet Operations reducers
   */
  .handleType(WalletActionTypes.GET_CURRENT_WALLET_OPERATIONS, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { wallets: { walletId: action.walletId, operations: {} } }),
  )

  .handleType(WalletActionTypes.GET_CURRENT_WALLET_OPERATIONS_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, operations: {} } },
      { error: action.error },
    ),
  )

  .handleType(WalletActionTypes.GET_CURRENT_WALLET_OPERATIONS_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      {
        wallets: {
          walletId: action.walletId,
          operations: {},
        },
      },
      { state: action.paginateOperations, error: undefined },
    ),
  )

  .handleType(WalletActionTypes.SET_CURRENT_WALLET_OPERATIONS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileWallets: {
              ...profile.state?.profileWallets,
              state: profile.state?.profileWallets?.state?.map((profileWallet) => {
                if (profileWallet.state?.wallet && profileWallet.state?.wallet.walletId === action.walletId) {
                  return {
                    ...profileWallet,
                    state: {
                      ...profileWallet.state,
                      paginateOperations: {
                        state: action.paginateOperations,
                      },
                    },
                  };
                }

                return profileWallet;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Set Current Wallet reducer
   */
  .handleType(WalletActionTypes.SET_CURRENT_WALLET, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileWallets: {
              ...profile.state?.profileWallets,
              currentWalletId: action.currentWalletId,
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Deactivate Wallet reducers
   */
  .handleType(WalletActionTypes.DEACTIVATE_WALLET, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { wallets: { walletId: action.walletId, deactivate: {} } }),
  )

  .handleType(WalletActionTypes.DEACTIVATE_WALLET_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, deactivate: {} } },
      { error: action.error },
    ),
  )

  .handleType(WalletActionTypes.DEACTIVATE_WALLET_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.wallet.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileWallets: {
              ...profile.state?.profileWallets,
              state: profile.state?.profileWallets?.state?.map((profileWallet) => {
                if (profileWallet.state?.wallet && profileWallet.state?.wallet.walletId === action.wallet.walletId) {
                  return {
                    ...profileWallet,
                    state: {
                      ...profileWallet.state,
                      wallet: action.wallet,
                      deactivate: {
                        isLoading: false,
                        error: undefined,
                      },
                    },
                  };
                }

                return profileWallet;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })
  /**
   * Unset Current Wallet reducer
   */
  .handleType(WalletActionTypes.UNSET_WALLETS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileWallets: {
              ...profile.state?.profileWallets,
              currentWalletId: undefined,
              state: [],
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })
  /**
   * Get Wallet account detail reducers
   */
  .handleType(WalletActionTypes.GET_WALLET_ACCOUNT_DETAILS, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { wallets: { walletId: action.walletId, accountDetails: {} } }),
  )

  .handleType(WalletActionTypes.GET_WALLET_ACCOUNT_DETAILS_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, accountDetails: {} } },
      { error: action.error },
    ),
  )

  .handleType(WalletActionTypes.GET_WALLET_ACCOUNT_DETAILS_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, accountDetails: {} } },
      { state: action.walletAccountDetail, error: undefined },
    ),
  )

  /**
   * Get Wallet statement reducers
   */
  .handleType(WalletActionTypes.GET_WALLET_STATEMENT, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { wallets: { walletId: action.walletId, statements: {} } }),
  )

  .handleType(WalletActionTypes.GET_WALLET_STATEMENT_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, statements: {} } },
      { error: action.error },
    ),
  )

  .handleType(WalletActionTypes.GET_WALLET_STATEMENT_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, statements: {} } },
      { state: action.statement, error: undefined },
    ),
  ) /**
   * Get Wallet domiciliation certificate reducers
   */
  .handleType(WalletActionTypes.GET_WALLET_DOMICILIATION_CERTIFICATE, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      wallets: { walletId: action.walletId, domiciliationCertificate: {} },
    }),
  )

  .handleType(WalletActionTypes.GET_WALLET_DOMICILIATION_CERTIFICATE_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, domiciliationCertificate: {} } },
      { error: action.error },
    ),
  )

  .handleType(WalletActionTypes.GET_WALLET_DOMICILIATION_CERTIFICATE_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, domiciliationCertificate: {} } },
      { state: action.domiciliationCertificate, error: undefined },
    ),
  )
  /**
   * Get Wallet balance certificate reducers
   */
  .handleType(WalletActionTypes.GET_WALLET_BALANCE_CERTIFICATE, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { wallets: { walletId: action.walletId, balanceCertificate: {} } }),
  )

  .handleType(WalletActionTypes.GET_WALLET_BALANCE_CERTIFICATE_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, balanceCertificate: {} } },
      { error: action.error },
    ),
  )

  .handleType(WalletActionTypes.GET_WALLET_BALANCE_CERTIFICATE_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, balanceCertificate: {} } },
      { state: action.balanceCertificate, error: undefined },
    ),
  )
  /**
   * Get Wallet closure certificate reducers
   */
  .handleType(WalletActionTypes.GET_WALLET_CLOSURE_CERTIFICATE, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { wallets: { walletId: action.walletId, closureCertificate: {} } }),
  )

  .handleType(WalletActionTypes.GET_WALLET_CLOSURE_CERTIFICATE_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, closureCertificate: {} } },
      { error: action.error },
    ),
  )

  .handleType(WalletActionTypes.GET_WALLET_CLOSURE_CERTIFICATE_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { wallets: { walletId: action.walletId, closureCertificate: {} } },
      { state: action.closureCertificate, error: undefined },
    ),
  )
  /**
   * Get operation proofOfPayout
   */
  .handleType(WalletActionTypes.GET_OPERATION_PROOF_OF_PAYOUT, (state, action) =>
    handleGetProofOfPayout(state, action.userId, action.walletId, action.objectId, { isLoading: true }),
  )

  .handleType(WalletActionTypes.GET_OPERATION_PROOF_OF_PAYOUT_SUCCESS, (state, action) =>
    handleGetProofOfPayout(state, action.userId, action.walletId, action.objectId, {
      proofOfPayout: action.proofOfPayout,
      isLoading: false,
    }),
  )

  .handleType(WalletActionTypes.GET_OPERATION_PROOF_OF_PAYOUT_FAIL, (state, action) =>
    handleGetProofOfPayout(state, action.userId, action.walletId, action.objectId, {
      isLoading: true,
      error: action.error,
    }),
  );

export const handleAsyncProfileReducer = (
  currentState: IProfileState,
  userId: number,
  selectors: IHandleAsynProfileSelectors,
  actions?: IHandleAsynProfileActions,
): IProfileState => {
  const profilesState = currentState.profiles.state?.map((profileState): IState<IProfile> => {
    if (profileState.state?.user.state?.userId === userId) {
      if (selectors.wallets.walletId) {
        if (selectors.wallets.statements) {
          return handleWalletStatements(profileState, selectors, actions);
        }

        if (selectors.wallets.accountDetails) {
          return handleWalletAccountDetails(profileState, selectors, actions);
        }
        if (selectors.wallets.domiciliationCertificate) {
          return handleWalletCertificates(profileState, selectors, 'domiciliationCertificate', actions);
        }
        if (selectors.wallets.balanceCertificate) {
          return handleWalletCertificates(profileState, selectors, 'balanceCertificate', actions);
        }
        if (selectors.wallets.closureCertificate) {
          return handleWalletCertificates(profileState, selectors, 'closureCertificate', actions);
        }

        if (selectors.wallets.operations) {
          return handleWalletOperations(profileState, selectors, actions);
        }
        if (selectors.wallets.report) {
          return handleWalletOperationsReport(profileState, selectors, actions);
        }

        if (selectors.wallets.deactivate) {
          return handleWalletDeactivate(profileState, selectors, actions);
        }

        if (selectors.wallets.simulation) {
          if (selectors.wallets.simulation.sctr)
            return handleWalletSimulation(profileState, 'sctr', selectors.wallets.walletId, actions);
          if (selectors.wallets.simulation.operation)
            return handleWalletSimulation(profileState, 'operation', selectors.wallets.walletId, actions);
        }

        return handleWallet(profileState, selectors, actions);
      }

      if (selectors.wallets.create) {
        return handleCreateWallet(profileState, actions);
      }

      return handleWallets(profileState, actions);
    }
    return profileState;
  });

  return {
    ...currentState,
    profiles: {
      ...currentState.profiles,
      state: profilesState,
    },
  };
};

function handleCreateWallet(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileWallets: {
        ...profileState.state?.profileWallets,
        create: {
          isLoading: !profileState.state?.profileWallets?.create?.isLoading,
          ...action,
        },
      },
    },
  };
}

function handleWallets(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileWallets: {
        ...profileState.state?.profileWallets,
        isLoading: !profileState.state?.profileWallets?.isLoading,
        ...action,
      },
    },
  };
}

function handleWallet(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileWallets: {
        ...profileState.state?.profileWallets,
        state: profileState.state?.profileWallets?.state?.map((profileWallet) => {
          if (profileWallet.state?.wallet && profileWallet.state?.wallet.walletId === selectors.wallets?.walletId) {
            return {
              ...profileWallet,
              state: {
                ...profileWallet.state,
              },
              isLoading: !profileWallet.isLoading,
              ...action,
            };
          }
          return profileWallet;
        }),
      },
    },
  };
}

function handleWalletAccountDetails(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileWallets: {
        ...profileState.state?.profileWallets,
        state: profileState.state?.profileWallets?.state?.map((profileWallet) => {
          if (profileWallet.state?.wallet && profileWallet.state?.wallet.walletId === selectors.wallets?.walletId) {
            return {
              ...profileWallet,
              state: {
                ...profileWallet.state,
                accountDetails: {
                  ...profileWallet.state?.accountDetails,
                  isLoading: !profileWallet.state?.accountDetails?.isLoading,
                  ...action,
                },
              },
            };
          }
          return profileWallet;
        }),
      },
    },
  };
}

function handleWalletStatements(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileWallets: {
        ...profileState.state?.profileWallets,
        state: profileState.state?.profileWallets?.state?.map((profileWallet): IState<IProfileWallet> => {
          if (profileWallet.state?.wallet && profileWallet.state?.wallet.walletId === selectors.wallets?.walletId) {
            return {
              ...profileWallet,
              state: {
                ...profileWallet.state,
                statement: {
                  ...profileWallet.state?.statement,
                  isLoading: !profileWallet.state?.statement?.isLoading,
                  ...action,
                },
              },
            };
          }

          return profileWallet;
        }),
      },
    },
  };
}

function handleWalletCertificates(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
  certificate: 'domiciliationCertificate' | 'balanceCertificate' | 'closureCertificate',
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileWallets: {
        ...profileState.state?.profileWallets,
        state: profileState.state?.profileWallets?.state?.map((profileWallet): IState<IProfileWallet> => {
          if (profileWallet.state?.wallet && profileWallet.state?.wallet.walletId === selectors.wallets?.walletId) {
            return {
              ...profileWallet,
              state: {
                ...profileWallet.state,
                [certificate]: {
                  ...profileWallet.state?.[certificate],
                  isLoading: !profileWallet.state?.[certificate]?.isLoading,
                  ...action,
                },
              },
            };
          }

          return profileWallet;
        }),
      },
    },
  };
}

function handleWalletOperations(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileWallets: {
        ...profileState.state?.profileWallets,
        state: profileState.state?.profileWallets?.state?.map((profileWallet) => {
          if (profileWallet.state?.wallet && profileWallet.state?.wallet.walletId === selectors.wallets?.walletId) {
            return {
              ...profileWallet,
              state: {
                ...profileWallet.state,
                paginateOperations: {
                  ...profileWallet.state?.paginateOperations,
                  isLoading: !profileWallet.state?.paginateOperations?.isLoading,
                  ...action,
                },
              },
            };
          }

          return profileWallet;
        }),
      },
    },
  };
}

function handleWalletOperationsReport(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      operations: {
        ...profileState.state?.operations,
        isLoading: !profileState.state?.operations?.isLoading,
        error: action?.error,
        state: {
          ...profileState.state?.operations?.state,
          [selectors.wallets.walletId]: { ...action?.state },
        },
      },
    },
  };
}

function handleWalletDeactivate(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileWallets: {
        ...profileState.state?.profileWallets,
        state: profileState.state?.profileWallets?.state?.map((profileWallet) => {
          if (profileWallet.state?.wallet && profileWallet.state?.wallet.walletId === selectors.wallets?.walletId) {
            return {
              ...profileWallet,
              state: {
                ...profileWallet.state,
                deactivate: {
                  ...profileWallet.state?.deactivate,
                  isLoading: !profileWallet.state?.deactivate?.isLoading,
                  ...action,
                },
              },
            };
          }

          return profileWallet;
        }),
      },
    },
  };
}

function handleWalletSimulation(
  profileState: IState<IProfile>,
  type: 'sctr' | 'operation',
  walletId: number,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;
  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileWallets: {
        ...profileState.state?.profileWallets,
        state: profileState.state?.profileWallets?.state?.map((profileWallet) => {
          if (profileWallet.state?.wallet && profileWallet.state?.wallet.walletId === walletId) {
            return {
              ...profileWallet,
              state: {
                ...profileWallet.state,
                simulation: {
                  ...profileWallet.state?.simulation,
                  [type]: {
                    ...profileWallet.state?.simulation?.[type],
                    isLoading: !profileWallet.state?.simulation?.[type]?.isLoading,
                    ...action,
                  },
                },
              },
            };
          }

          return profileWallet;
        }),
      },
    },
  };
}

function handleGetProofOfPayout(
  state: IProfileState,
  userId: number,
  walletId: number,
  objectId: string,
  action: {
    proofOfPayout?: IOperationProofOfPayoutDownload;
    error?: TAppError;
    isLoading: boolean;
  },
): IProfileState {
  const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
    if (profile.state?.user.state?.userId === userId) {
      return {
        ...profile,
        state: {
          ...profile.state,
          profileWallets: {
            ...profile.state?.profileWallets,
            state: profile.state?.profileWallets?.state?.map((profileWallet) => {
              if (profileWallet.state?.wallet && profileWallet.state?.wallet.walletId === walletId) {
                return {
                  ...profileWallet,
                  state: {
                    ...profileWallet.state,
                    paginateOperations: {
                      ...profileWallet.state.paginateOperations,
                      state: {
                        ...profileWallet.state.paginateOperations?.state,
                        elements: profileWallet.state.paginateOperations?.state?.elements.map((operation) => {
                          if (operation?.objectId === objectId) {
                            return {
                              ...operation,
                              proofOfPayout: {
                                ...operation.proofOfPayout,
                                ...(action.proofOfPayout ? { state: action.proofOfPayout } : {}),
                                ...(action.error ? { error: action.error } : {}),
                                isLoading: action.isLoading,
                              },
                            };
                          }
                          return operation;
                        }),
                      },
                    },
                  },
                };
              }

              return profileWallet;
            }),
          },
        },
      };
    }

    return profile;
  });

  return {
    ...state,
    profiles: {
      state: profiles,
    },
  };
}
